// src/services/messagingService.ts
import axios, { AxiosInstance } from 'axios';
import { MESSAGING_CONFIG } from '../config/messaging';
import {
  Conversation,
  Message,
  Group,
  GroupMessage,
  GroupMember,
  Contact,
  ContactRequest,
  Block,
  MessagingUser,
  SendMessagePayload,
  CreateConversationPayload,
  CreateGroupPayload,
  UpdateGroupPayload,
  AddGroupMemberPayload,
  ChangeGroupMemberRolePayload,
  SendContactRequestPayload,
  BlockUserPayload,
  AddReactionPayload,
} from '../types/messaging';

const { ENDPOINTS, DEFAULT_MESSAGE_LIMIT } = MESSAGING_CONFIG;

// Create a separate axios instance for the messaging API
const messagingApi: AxiosInstance = axios.create({
  baseURL: MESSAGING_CONFIG.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
messagingApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
messagingApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 - redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const messagingService = {
  // ========== Conversations ==========

  async getConversations(): Promise<Conversation[]> {
    // Backend returns: { conversations: [...], total, page, page_size, has_more }
    // Each conversation has 'participant' not 'other_user'
    interface BackendConversation {
      id: number;
      participant: { id: number; name: string };
      last_message?: any;
      unread_count: number;
      created_at: string;
      updated_at: string;
    }
    interface ConversationListResponse {
      conversations: BackendConversation[];
      total: number;
      page: number;
      page_size: number;
      has_more: boolean;
    }
    const response = await messagingApi.get<ConversationListResponse>(ENDPOINTS.CONVERSATIONS + '/');
    const data = response.data;
    // Map 'participant' to 'other_user' to match frontend type
    return (data.conversations || []).map((conv) => ({
      id: conv.id,
      created_at: conv.created_at,
      updated_at: conv.updated_at,
      other_user: {
        id: conv.participant.id,
        name: conv.participant.name,
      },
      last_message: conv.last_message,
      unread_count: conv.unread_count,
    }));
  },

  async createConversation(payload: CreateConversationPayload): Promise<Conversation> {
    // API expects { participant_id: <number> }
    const response = await messagingApi.post<Conversation>(ENDPOINTS.CONVERSATIONS, {
      participant_id: payload.other_user_id,
    });
    return response.data;
  },

  async getConversation(conversationId: number): Promise<Conversation> {
    const response = await messagingApi.get<Conversation>(`${ENDPOINTS.CONVERSATIONS}/${conversationId}`);
    return response.data;
  },

  async deleteConversation(conversationId: number): Promise<void> {
    await messagingApi.delete(`${ENDPOINTS.CONVERSATIONS}/${conversationId}`);
  },

  // ========== Messages (DM) ==========

  async getMessages(
    conversationId: number,
    limit: number = DEFAULT_MESSAGE_LIMIT,
    beforeId?: number
  ): Promise<{ messages: Message[]; has_more: boolean }> {
    const params: Record<string, any> = { limit };
    if (beforeId) {
      params.before_id = beforeId;
    }
    const response = await messagingApi.get<{ messages: Message[]; has_more: boolean }>(
      ENDPOINTS.MESSAGES(conversationId),
      { params }
    );
    return response.data;
  },

  async sendMessage(conversationId: number, payload: SendMessagePayload): Promise<Message> {
    const response = await messagingApi.post<Message>(ENDPOINTS.MESSAGES(conversationId), payload);
    return response.data;
  },

  async editMessage(conversationId: number, messageId: number, content: string): Promise<Message> {
    const response = await messagingApi.patch<Message>(
      ENDPOINTS.MESSAGE(conversationId, messageId),
      { content }
    );
    return response.data;
  },

  async deleteMessage(conversationId: number, messageId: number): Promise<void> {
    await messagingApi.delete(ENDPOINTS.MESSAGE(conversationId, messageId));
  },

  async markMessagesAsRead(conversationId: number): Promise<void> {
    // Send empty object as body - FastAPI may require a body for POST
    await messagingApi.post(ENDPOINTS.MESSAGES_READ(conversationId), {});
  },

  async uploadMessageMedia(
    conversationId: number,
    file: File,
    type: 'image' | 'audio' | 'file',
    replyToId?: number,
    onProgress?: (progress: number) => void
  ): Promise<Message> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('type', type);
    if (replyToId) {
      formData.append('reply_to_id', replyToId.toString());
    }

    const response = await messagingApi.post<Message>(
      ENDPOINTS.MESSAGES_UPLOAD(conversationId),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data;
  },

  async addReaction(conversationId: number, messageId: number, payload: AddReactionPayload): Promise<void> {
    await messagingApi.post(ENDPOINTS.MESSAGE_REACTIONS(conversationId, messageId), payload);
  },

  async removeReaction(conversationId: number, messageId: number): Promise<void> {
    await messagingApi.delete(ENDPOINTS.MESSAGE_REACTIONS(conversationId, messageId));
  },

  // ========== Groups ==========

  async getGroups(): Promise<Group[]> {
    // Backend returns: { groups: [...], total, page, page_size, has_more }
    interface GroupListResponse {
      groups: Group[];
      total: number;
      page: number;
      page_size: number;
      has_more: boolean;
    }
    const response = await messagingApi.get<GroupListResponse>(ENDPOINTS.GROUPS + '/');
    return response.data?.groups || [];
  },

  async createGroup(payload: CreateGroupPayload): Promise<Group> {
    const response = await messagingApi.post<Group>(ENDPOINTS.GROUPS, payload);
    return response.data;
  },

  async getGroup(groupId: number): Promise<Group> {
    const response = await messagingApi.get<Group>(ENDPOINTS.GROUP(groupId));
    return response.data;
  },

  async updateGroup(groupId: number, payload: UpdateGroupPayload): Promise<Group> {
    const response = await messagingApi.put<Group>(ENDPOINTS.GROUP(groupId), payload);
    return response.data;
  },

  async deleteGroup(groupId: number): Promise<void> {
    await messagingApi.delete(ENDPOINTS.GROUP(groupId));
  },

  async getGroupMembers(groupId: number): Promise<GroupMember[]> {
    const response = await messagingApi.get<GroupMember[]>(ENDPOINTS.GROUP_MEMBERS(groupId));
    return response.data;
  },

  async addGroupMember(groupId: number, payload: AddGroupMemberPayload): Promise<GroupMember> {
    const response = await messagingApi.post<GroupMember>(ENDPOINTS.GROUP_MEMBERS(groupId), payload);
    return response.data;
  },

  async removeGroupMember(groupId: number, userId: number): Promise<void> {
    await messagingApi.delete(ENDPOINTS.GROUP_MEMBER(groupId, userId));
  },

  async changeGroupMemberRole(groupId: number, userId: number, payload: ChangeGroupMemberRolePayload): Promise<GroupMember> {
    const response = await messagingApi.put<GroupMember>(
      ENDPOINTS.GROUP_MEMBER_ROLE(groupId, userId),
      payload
    );
    return response.data;
  },

  async leaveGroup(groupId: number): Promise<void> {
    await messagingApi.post(ENDPOINTS.GROUP_LEAVE(groupId), {});
  },

  // ========== Group Messages ==========

  async getGroupMessages(
    groupId: number,
    limit: number = DEFAULT_MESSAGE_LIMIT,
    beforeId?: number
  ): Promise<{ messages: GroupMessage[]; has_more: boolean }> {
    const params: Record<string, any> = { limit };
    if (beforeId) {
      params.before_id = beforeId;
    }
    const response = await messagingApi.get<{ messages: GroupMessage[]; has_more: boolean }>(
      ENDPOINTS.GROUP_MESSAGES(groupId),
      { params }
    );
    return response.data;
  },

  async sendGroupMessage(groupId: number, payload: SendMessagePayload): Promise<GroupMessage> {
    const response = await messagingApi.post<GroupMessage>(ENDPOINTS.GROUP_MESSAGES(groupId), payload);
    return response.data;
  },

  async editGroupMessage(groupId: number, messageId: number, content: string): Promise<GroupMessage> {
    const response = await messagingApi.patch<GroupMessage>(
      ENDPOINTS.GROUP_MESSAGE(groupId, messageId),
      { content }
    );
    return response.data;
  },

  async deleteGroupMessage(groupId: number, messageId: number): Promise<void> {
    await messagingApi.delete(ENDPOINTS.GROUP_MESSAGE(groupId, messageId));
  },

  async markGroupMessagesAsRead(groupId: number): Promise<void> {
    // Send empty object as body - FastAPI may require a body for POST
    await messagingApi.post(ENDPOINTS.GROUP_MESSAGES_READ(groupId), {});
  },

  async uploadGroupMessageMedia(
    groupId: number,
    file: File,
    type: 'image' | 'audio' | 'file',
    replyToId?: number,
    onProgress?: (progress: number) => void
  ): Promise<GroupMessage> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('type', type);
    if (replyToId) {
      formData.append('reply_to_id', replyToId.toString());
    }

    const response = await messagingApi.post<GroupMessage>(
      ENDPOINTS.GROUP_MESSAGES_UPLOAD(groupId),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        },
      }
    );
    return response.data;
  },

  async addGroupMessageReaction(groupId: number, messageId: number, payload: AddReactionPayload): Promise<void> {
    await messagingApi.post(ENDPOINTS.GROUP_MESSAGE_REACTIONS(groupId, messageId), payload);
  },

  async removeGroupMessageReaction(groupId: number, messageId: number): Promise<void> {
    await messagingApi.delete(ENDPOINTS.GROUP_MESSAGE_REACTIONS(groupId, messageId));
  },

  // ========== Contacts ==========

  async getContacts(): Promise<Contact[]> {
    // API returns { contacts: [...], total: number }
    const response = await messagingApi.get<{ contacts: Contact[]; total: number }>(ENDPOINTS.CONTACTS);
    return response.data?.contacts || [];
  },

  async getContactRequests(): Promise<ContactRequest[]> {
    // Get pending received contact requests
    // API returns { requests: [...], total: number }
    const response = await messagingApi.get<{ requests: ContactRequest[]; total: number }>(ENDPOINTS.CONTACT_REQUESTS_PENDING);
    return response.data?.requests || [];
  },

  async sendContactRequest(payload: SendContactRequestPayload): Promise<ContactRequest> {
    // Backend expects { user_id: <number> }
    const response = await messagingApi.post<ContactRequest>(ENDPOINTS.CONTACT_REQUEST, {
      user_id: payload.to_user_id,
    });
    return response.data;
  },

  async acceptContactRequest(requestId: number): Promise<Contact> {
    const response = await messagingApi.post<Contact>(ENDPOINTS.CONTACT_REQUEST_ACCEPT(requestId));
    return response.data;
  },

  async rejectContactRequest(requestId: number): Promise<void> {
    await messagingApi.post(ENDPOINTS.CONTACT_REQUEST_REJECT(requestId), {});
  },

  // ========== Blocks ==========

  async getBlocks(): Promise<Block[]> {
    const response = await messagingApi.get<Block[]>(ENDPOINTS.BLOCKS);
    return response.data;
  },

  async blockUser(payload: BlockUserPayload): Promise<Block> {
    const response = await messagingApi.post<Block>(ENDPOINTS.BLOCKS, payload);
    return response.data;
  },

  async unblockUser(blockId: number): Promise<void> {
    await messagingApi.delete(ENDPOINTS.BLOCK(blockId));
  },

  // ========== Users ==========

  async searchUsers(query: string): Promise<MessagingUser[]> {
    // API returns { users: [...], total, page, page_size }
    const response = await messagingApi.get<{ users: MessagingUser[]; total: number }>(ENDPOINTS.USERS_SEARCH, {
      params: { search: query, page: 1, page_size: 20 },
    });
    return response.data?.users || [];
  },

  async getUser(userId: number): Promise<MessagingUser> {
    const response = await messagingApi.get<MessagingUser>(ENDPOINTS.USER(userId));
    return response.data;
  },
};

export default messagingService;
